import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";
import type { AuthResponse, AuthTokenCreationQuery } from "@/Modules/Models/Domain/Auth";
import type { AuthTokenApi } from "@/Modules/Models/Api/Auth";

import { LocalStorageService } from "@/Services/LocalStorageService";
import { login } from "@/Services/TherapyApi/Auth";

import { isApiError } from "@/Services/Api/Typeguards/isApiError";

import useAuthStore from "../Store";

import { isValidString } from "@/Utils/String";

async function createUserAuthTokenAH(
  authTokenUiCreationQuery: AuthTokenCreationQuery
): Promise<AuthResponse | undefined> {
  const authStore = useAuthStore();
  let authResp: AuthResponse;

  authStore.isLoading = true;

  const authTokenCreationQuery: AuthTokenCreationQuery = {
    email: authTokenUiCreationQuery.email,
    password: authTokenUiCreationQuery.password,
  };

  try {
    const response = await login(authTokenCreationQuery);

    if (isValidString(response.token)) {
      const authToken: AuthTokenApi = {
        token: response.token,
      };

      saveTokenInLocalStorage(authToken);

      authStore.userToken = response.token;
      authResp = { token: response.token };
    } else if (isValidString(response.hash_2F)) {
      authStore.userToken = null;
      authResp = { hash2F: response.hash_2F };
    } else {
      throw new Error("Invalid auth response");
    }
  } catch (error: unknown) {
    const code = isApiError(error) ? error["error_code"] : null;

    let message: string;

    if (code === ApiErrorCodes.ACCESS_BLOCKED) {
      message = "login.too_many_login_attempts";
    } else {
      message = "login.error.the_data_is_not_correct";
    }

    authStore.notification = {
      type: NotificationType.ERROR,
      code: code,
      message: message,
    };

    return undefined;
  } finally {
    authStore.isLoading = false;
  }

  return authResp;
}

function saveTokenInLocalStorage(authToken: AuthTokenApi) {
  const authLocalStorageService: LocalStorageService = new LocalStorageService(LocalStorageNamespace.AUTH);
  const LOCAL_STORAGE_AUTH_TOKEN_KEY: string = LocalStorageKey.USER_TOKEN;

  authLocalStorageService.set(LOCAL_STORAGE_AUTH_TOKEN_KEY, authToken.token);
}

export default createUserAuthTokenAH;
